/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import "./index.css";

import {
  $createLinkNode,
  $isAutoLinkNode,
  $isLinkNode,
  TOGGLE_LINK_COMMAND,
} from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isLineBreakNode,
  $isRangeSelection,
  $setSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  KEY_ESCAPE_COMMAND,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import * as React from "react";
import { Dispatch, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button, Input } from "@/components/Elements";
import { PencilIcon } from "@heroicons/react/24/solid";
import { ExternalLink } from "lucide-react";
import { TbCheck, TbTrashFilled, TbX } from "react-icons/tb";
import { getDOMRangeRect } from "../../utils/getDOMRangeRect";
import { getSelectedNode } from "../../utils/getSelectedNode";
import { setFloatingElemPositionForLinkEditor } from "../../utils/setFloatingElemPositionForLinkEditor";
import { sanitizeUrl } from "../../utils/url";

function FloatingLinkEditor({
  editor,
  isLink,
  setIsLink,
  anchorElem,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  editor: LexicalEditor;
  isLink: boolean;
  setIsLink: Dispatch<boolean>;
  anchorElem: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [editedLinkUrl, setEditedLinkUrl] = useState("");
  const [lastSelection, setLastSelection] = useState<BaseSelection | null>(
    null
  );

  const updateLinkEditor = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection);
      const linkParent = $findMatchingParent(node, $isLinkNode);

      let url = "";
      if (linkParent) {
        url = linkParent.getURL();
      } else if ($isLinkNode(node)) {
        url = node.getURL();
      }
      setLinkUrl(url === "https://" ? "" : url);

      if (isLinkEditMode) {
        setEditedLinkUrl(url === "https://" ? "" : url);
      }
    }
    const editorElem = editorRef.current;
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (editorElem === null) {
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect = getDOMRangeRect(nativeSelection, rootElement);
      if (domRect) {
        domRect.y -= 36;
        setFloatingElemPositionForLinkEditor(domRect, editorElem, anchorElem);
      }
      setLastSelection(selection);
    } else if (!activeElement || activeElement.id !== "link-input") {
      if (rootElement !== null) {
        setFloatingElemPositionForLinkEditor(null, editorElem, anchorElem);
      }
      setLastSelection(null);
      setIsLinkEditMode(false);
      setLinkUrl("");
    }

    return true;
  }, [anchorElem, editor, setIsLinkEditMode, isLinkEditMode, linkUrl]);

  useEffect(() => {
    const scrollerElem = anchorElem.parentElement;

    const update = () => {
      editor.getEditorState().read(() => {
        updateLinkEditor();
      });
    };

    window.addEventListener("resize", update);

    if (scrollerElem) {
      scrollerElem.addEventListener("scroll", update);
    }

    return () => {
      window.removeEventListener("resize", update);

      if (scrollerElem) {
        scrollerElem.removeEventListener("scroll", update);
      }
    };
  }, [anchorElem.parentElement, editor, updateLinkEditor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor();
          setLastSelection($getSelection());
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        () => {
          if (isLink) {
            setIsLink(false);
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor, updateLinkEditor, setIsLink, isLink]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor();
    });
  }, [editor, updateLinkEditor]);

  useEffect(() => {
    if (isLinkEditMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isLinkEditMode, isLink]);

  const monitorInputInteraction = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleLinkSubmission();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setIsLinkEditMode(false);
    }
  };

  const handleLinkSubmission = () => {
    if (lastSelection !== null) {
      if (editedLinkUrl !== "") {
        let sanitizedUrl = sanitizeUrl(editedLinkUrl);
        // Ensure the URL has a protocol
        if (
          !sanitizedUrl.startsWith("http://") &&
          !sanitizedUrl.startsWith("https://")
        ) {
          sanitizedUrl = "https://" + sanitizedUrl;
        }
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizedUrl);
        editor.update(() => {
          if ($isRangeSelection(lastSelection)) {
            let parent = getSelectedNode(lastSelection) as any;
            if ($isLinkNode(parent) || $isAutoLinkNode(parent)) {
              const linkNode = $createLinkNode(sanitizedUrl, {
                rel: parent.__rel,
                target: parent.__target,
                title: parent.__title,
              });
              parent.replace(linkNode, true);
            } else {
              parent = parent.getParent();
              if ($isLinkNode(parent) || $isAutoLinkNode(parent)) {
                const linkNode = $createLinkNode(sanitizedUrl, {
                  rel: parent.__rel,
                  target: parent.__target,
                  title: parent.__title,
                });
                parent.replace(linkNode, true);
              }
            }
          }
        });
      }
      setEditedLinkUrl("https://");
      setIsLinkEditMode(false);
    }
  };

  return (
    <div ref={editorRef} className="link-editor">
      {!isLink ? null : isLinkEditMode ? (
        <div className="flex w-fit items-center space-x-1 align-middle h-10 z-50 origin-top-right divide-y divide-zinc-100 dark:divide-zinc-600 rounded-md backdrop-blur-lg dark:bg-zinc-900 shadow-md dark:shadow-lg ring-1 ring-zinc-900 dark:ring-1 dark:ring-zinc-50 dark:ring-opacity-15 ring-opacity-5 focus:outline-none pr-2">
          <Input
            placeholder="Enter link URL"
            ref={inputRef}
            id="link-input"
            className="ring-transparent transition hover:ring-transparent dark:ring-transparent dark:hover:ring-transparent lg:flex focus:[&:not(:focus-visible)]:outline-none focus:outline-none file:-mx-3 file:-my-2 file:overflow-hidden file:bg-zinc-100 dark:file:bg-zinc-700 file:px-2.5 file:outline-none file:border-none file:py-3 file:text-zinc-700 dark:file:text-zinc-100 file:transition file:duration-150 file:ease-in-out file:[margin-inline-end:0.75rem] file:[border-inline-end-width:1px] border-none !outline-none focus:ring-transparent focus:ring-inset"
            containerClassName="w-fit"
            value={editedLinkUrl}
            onChange={(value) => {
              setEditedLinkUrl(value);
            }}
            onKeyDown={(event) => {
              monitorInputInteraction(event);
            }}
            onBlur={() => {
              if (editedLinkUrl === "") {
                editor.update(() => {
                  $setSelection(lastSelection?.clone() ?? null);
                });
                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
              } else {
                handleLinkSubmission();
              }
            }}
          />
          <Button
            variant="buttonIcon"
            buttonIcon={<TbX />}
            size="sm"
            tooltipContent="Cancel"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (linkUrl === "") {
                editor.update(() => {
                  $setSelection(lastSelection?.clone() ?? null);
                  editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
                });
              }

              setIsLinkEditMode(false);
            }}
          />

          <Button
            variant="buttonIcon"
            size="sm"
            tooltipContent="Save"
            buttonIcon={<TbCheck />}
            disabled={editedLinkUrl === ""}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              if (editedLinkUrl === "") {
                event.preventDefault();
              } else {
                handleLinkSubmission();
              }
            }}
          />
        </div>
      ) : (
        <div className="flex items-center space-x-1 align-middle h-10 z-50 origin-top-right divide-y divide-zinc-100 dark:divide-zinc-600 rounded-md backdrop-blur-lg bg-white/90 dark:bg-zinc-800 shadow-md dark:shadow-lg ring-1 ring-zinc-900 dark:ring-1 dark:ring-zinc-50 dark:ring-opacity-15 ring-opacity-5 focus:outline-none px-1">
          <a
            href={sanitizeUrl(linkUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-zinc-700 dark:text-white pl-2 hover:underline truncate"
            style={{
              minWidth: "180px",
              maxWidth: "260px",
            }}
          >
            {linkUrl}
          </a>
          <Button
            variant="buttonIcon"
            tooltipContent="Open link in new tab"
            buttonIcon={<ExternalLink />}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              window.open(sanitizeUrl(linkUrl), "_blank");
            }}
          />
          <Button
            variant="buttonIcon"
            tooltipContent="Edit link"
            buttonIcon={<PencilIcon />}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setEditedLinkUrl(linkUrl);
              setIsLinkEditMode(true);
            }}
          />
          <Button
            variant="buttonIcon"
            tooltipContent="Remove link"
            role="button"
            tabIndex={0}
            buttonIcon={<TbTrashFilled />}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              editor.update(() => {
                $setSelection(lastSelection?.clone() ?? null);

                editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
              });
            }}
          />
        </div>
      )}
    </div>
  );
}

function useFloatingLinkEditorToolbar(
  editor: LexicalEditor,
  anchorElem: HTMLElement,
  isLinkEditMode: boolean,
  setIsLinkEditMode: Dispatch<boolean>
): JSX.Element | null {
  const [activeEditor, setActiveEditor] = useState(editor);
  const [isLink, setIsLink] = useState(false);

  useEffect(() => {
    function updateToolbar() {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const focusNode = getSelectedNode(selection);
        const focusLinkNode = $findMatchingParent(focusNode, $isLinkNode);
        const focusAutoLinkNode = $findMatchingParent(
          focusNode,
          $isAutoLinkNode
        );
        if (!(focusLinkNode || focusAutoLinkNode)) {
          setIsLink(false);
          return;
        }
        const badNode = selection.getNodes().find((node) => {
          const linkNode = $findMatchingParent(node, $isLinkNode);
          const autoLinkNode = $findMatchingParent(node, $isAutoLinkNode);
          if (
            !linkNode?.is(focusLinkNode) &&
            !autoLinkNode?.is(focusAutoLinkNode) &&
            !linkNode &&
            !autoLinkNode &&
            !$isLineBreakNode(node)
          ) {
            return node;
          }
        });
        if (!badNode) {
          setIsLink(true);
        } else {
          setIsLink(false);
        }
      }
    }
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, newEditor) => {
          updateToolbar();
          setActiveEditor(newEditor);
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CLICK_COMMAND,
        (payload) => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const node = getSelectedNode(selection);
            const linkNode = $findMatchingParent(node, $isLinkNode);
            if ($isLinkNode(linkNode) && (payload.metaKey || payload.ctrlKey)) {
              window.open(linkNode.getURL(), "_blank");
              return true;
            }
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor]);

  return createPortal(
    <FloatingLinkEditor
      editor={activeEditor}
      isLink={isLink}
      anchorElem={anchorElem}
      setIsLink={setIsLink}
      isLinkEditMode={isLinkEditMode}
      setIsLinkEditMode={setIsLinkEditMode}
    />,
    anchorElem
  );
}

export default function FloatingLinkEditorPlugin({
  anchorElem = document.body,
  isLinkEditMode,
  setIsLinkEditMode,
}: {
  anchorElem?: HTMLElement;
  isLinkEditMode: boolean;
  setIsLinkEditMode: Dispatch<boolean>;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  return useFloatingLinkEditorToolbar(
    editor,
    anchorElem,
    isLinkEditMode,
    setIsLinkEditMode
  );
}
